import { Button, Group, Modal, SegmentedControl, Stack, Text } from "@mantine/core";
import { useEffect, useState } from "react";

import {
    ApplicationLayoutMode,
    ApplicationTheme,
    type IUserPreferences
} from "src/contexts/preferences/UserPreferencesContext";
import useUserPreferences from "src/hooks/preferences/useUserPreferences";

/**
 * Defines the inputs supplied to this component.
 */
interface IPreferencesModalProps {
    opened: boolean;
    onClose: () => void;
}

/**
 * Edits layout and theme in a local draft before applying them.
 *
 * Postconditions: Opening or changing saved preferences while open requests a fresh draft.
 * Cancel closes without saving. Save forwards the draft to the provider before requesting close.
 * Assumptions: Segmented controls supply supported values; casts do not perform validation.
 */
const PreferencesModal = ({ opened, onClose }: IPreferencesModalProps) => {
    const { preferences, setPreferences } = useUserPreferences();
    const [draft, setDraft] = useState<IUserPreferences>(preferences);

    useEffect(
        () => {
            if (opened) {
                setDraft(preferences);
            }
        },
        [opened, preferences]
    );

    /**
     * Requests the current draft as saved preferences, then invokes onClose.
     *
     * Postconditions: Successful return means both calls completed, not that a later render occurred.
     */
    const save = () => {
        setPreferences(draft);
        onClose();
    };

    return (
        <Modal
            opened={opened}
            onClose={onClose}
            title="Preferences"
            centered
        >
            <Stack>
                <Stack gap="xs">
                    <Text fw={500}>Layout</Text>
                    <SegmentedControl
                        fullWidth
                        value={draft.layout}
                        onChange={layout =>
                            setDraft(current => ({
                                ...current,
                                layout: layout as ApplicationLayoutMode
                            }))
                        }
                        data={[
                            { label: "Legacy", value: ApplicationLayoutMode.Legacy },
                            { label: "Modern", value: ApplicationLayoutMode.Modern }
                        ]}
                    />
                </Stack>
                <Stack gap="xs">
                    <Text fw={500}>Theme</Text>
                    <SegmentedControl
                        fullWidth
                        value={draft.theme}
                        onChange={theme =>
                            setDraft(current => ({
                                ...current,
                                theme: theme as ApplicationTheme
                            }))
                        }
                        data={[
                            { label: "Light", value: ApplicationTheme.Light },
                            { label: "Dark", value: ApplicationTheme.Dark }
                        ]}
                    />
                </Stack>
                <Group justify="flex-end">
                    <Button
                        variant="default"
                        onClick={onClose}
                    >
                        Cancel
                    </Button>
                    <Button onClick={save}>Save</Button>
                </Group>
            </Stack>
        </Modal>
    );
};

export default PreferencesModal;
